import { useState, useEffect, Component, type ErrorInfo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format, isValid } from "date-fns";
import { sv } from "date-fns/locale";
import { Eye, Package, MessageSquare, CalendarDays, RefreshCw, AlertTriangle } from "lucide-react";
import type { OrderWithItems } from "@shared/schema";

function safeFormatDate(value: unknown, fmt: string): string {
  try {
    if (!value) return "—";
    const d = new Date(value as string);
    if (!isValid(d)) return "—";
    return format(d, fmt, { locale: sv });
  } catch {
    return "—";
  }
}

class HistoryErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[HistoryErrorBoundary] Caught error:", error.message, error.stack, info.componentStack);
  }

  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || "Okänt fel";
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h1 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1rem" }}>Orderhistorik</h1>
          <p style={{ color: "#dc2626", marginBottom: "0.5rem" }}>Något gick fel</p>
          <p style={{ fontSize: "0.75rem", fontFamily: "monospace", wordBreak: "break-all", marginBottom: "1rem", color: "#6b7280" }}>{msg}</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: "0.5rem 1rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", cursor: "pointer" }}
          >
            Ladda om sidan
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function OrderDetailDialog({
  order,
  onClose,
}: {
  order: OrderWithItems;
  onClose: () => void;
}) {
  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { label: string; className: string }> = {
      pending: { label: "Väntande", className: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
      confirmed: { label: "Bekräftad", className: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
      in_production: { label: "I produktion", className: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
      packed: { label: "Färdig att leverera", className: "bg-teal-500/10 text-teal-600 border-teal-500/20" },
      delivered: { label: "Levererad", className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
      levererad: { label: "Levererad", className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
    };
    const variant = variants[status] || { label: status, className: "" };
    return <Badge variant="outline" className={variant.className}>{variant.label}</Badge>;
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Order #{order.orderNumber}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {order.scheduledDelivery && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <CalendarDays className="h-4 w-4 text-blue-600 shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Framtida order</p>
                {order.deliveryDate && (
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    Planerad leverans: {safeFormatDate(order.deliveryDate, "d MMMM yyyy")}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Beställningsdatum</span>
            <span>{safeFormatDate(order.orderDate, "d MMMM yyyy, HH:mm")}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Status</span>
            {getStatusBadge(order.status)}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produkt</TableHead>
                <TableHead className="text-right">Antal</TableHead>
                <TableHead className="text-right">Pris</TableHead>
                <TableHead className="text-right">Summa</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(order.items || []).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.product?.name || "Okänd produkt"}</TableCell>
                  <TableCell className="text-right">
                    {item.quantity} {getUnitLabel(item.product?.sellUnit || "pcs")}
                  </TableCell>
                  <TableCell className="text-right">{item.unitPrice} kr (ink.)</TableCell>
                  <TableCell className="text-right">{item.totalPrice} kr (ink.)</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {order.notes && (
            <div className="pt-4 border-t">
              <h4 className="font-medium mb-1 flex items-center gap-2 text-sm">
                <MessageSquare className="h-3.5 w-3.5" />
                Ditt meddelande
              </h4>
              <p className="text-sm text-muted-foreground italic">{order.notes}</p>
            </div>
          )}
          <div className="flex justify-between font-semibold pt-4 border-t">
            <span>Totalt</span>
            <span>{order.totalAmount} kr</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClientHistoryContent() {
  const [selectedOrder, setSelectedOrder] = useState<OrderWithItems | null>(null);
  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [invoicedOrderIds, setInvoicedOrderIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const [ordersRes, invoicedRes] = await Promise.all([
        fetch("/api/orders/my", { credentials: "include" }),
        fetch("/api/invoiced-order-ids", { credentials: "include" }),
      ]);

      if (!ordersRes.ok) {
        setIsError(true);
        setIsLoading(false);
        return;
      }

      const ordersData = await ordersRes.json();
      const invoicedData = invoicedRes.ok ? await invoicedRes.json() : [];

      setOrders(Array.isArray(ordersData) ? ordersData : []);
      setInvoicedOrderIds(Array.isArray(invoicedData) ? invoicedData : []);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const invoicedSet = new Set(invoicedOrderIds);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { label: string; className: string }> = {
      pending: { label: "Väntande", className: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
      confirmed: { label: "Bekräftad", className: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
      in_production: { label: "I produktion", className: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
      packed: { label: "Färdig att leverera", className: "bg-teal-500/10 text-teal-600 border-teal-500/20" },
      delivered: { label: "Levererad", className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
      levererad: { label: "Levererad", className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
    };
    const variant = variants[status] || { label: status, className: "" };
    return <Badge variant="outline" className={variant.className}>{variant.label}</Badge>;
  };

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Orderhistorik</h1>
        <p className="text-muted-foreground">
          Se dina tidigare beställningar
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-44" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="space-y-2 text-right">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                    <Skeleton className="h-6 w-24 rounded-full" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-8 text-center space-y-4" data-testid="history-error-state">
          <AlertTriangle className="h-10 w-10 mx-auto text-destructive" />
          <p className="font-medium text-foreground">Kunde inte hämta beställningar</p>
          <p className="text-sm text-muted-foreground">
            Kontrollera din anslutning och försök igen.
          </p>
          <Button
            variant="outline"
            onClick={() => fetchData()}
            data-testid="button-retry-orders"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Försök igen
          </Button>
        </div>
      ) : orders.length > 0 ? (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} className="hover-elevate">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Package className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium flex items-center gap-2 flex-wrap">
                        Order #{order.orderNumber}
                        {order.scheduledDelivery && (
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-xs" data-testid={`badge-scheduled-${order.id}`}>
                            <CalendarDays className="h-3 w-3 mr-1" />
                            Framtida order
                          </Badge>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {safeFormatDate(order.orderDate, "d MMMM yyyy, HH:mm")}
                      </p>
                      {order.scheduledDelivery && order.deliveryDate && (
                        <p className="text-sm font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1 mt-0.5">
                          <CalendarDays className="h-3.5 w-3.5" />
                          Leverans: {safeFormatDate(order.deliveryDate, "d MMMM yyyy")}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-semibold">{order.totalAmount} kr</p>
                      <p className="text-sm text-muted-foreground">
                        {order.items?.length || 0} produkt{(order.items?.length || 0) !== 1 ? "er" : ""}
                      </p>
                    </div>
                    {getStatusBadge(order.status)}
                    {invoicedSet.has(order.id) && (
                      <Badge variant="outline" className="bg-indigo-500/10 text-indigo-600 border-indigo-500/20" data-testid={`badge-invoiced-${order.id}`}>
                        Fakturerad
                      </Badge>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setSelectedOrder(order)}
                      data-testid={`button-view-order-${order.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-border bg-muted/50 p-12 text-center space-y-3" data-testid="history-empty-state">
          <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mx-auto">
            <Package className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="font-medium text-foreground">Inga beställningar än</p>
          <p className="text-sm text-muted-foreground">Dina beställningar visas här när du har gjort dem.</p>
        </div>
      )}

      {selectedOrder && (
        <OrderDetailDialog
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  );
}

export default function ClientHistory() {
  return (
    <HistoryErrorBoundary>
      <ClientHistoryContent />
    </HistoryErrorBoundary>
  );
}
